import LegalLayout from "@/pages/LegalLayout";

export default function Terms() {
  return (
    <LegalLayout title="Terms of Service" effective="March 2, 2026" testId="terms-page">
      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-10 mb-4">
          1. Acceptance of Terms
        </h2>
        <p>
          These Terms of Service (&quot;Terms&quot;) govern your access to and use of the
          website section3.ai and the Protego AI account health monitoring platform provided
          by Protego AI Inc. (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;). By
          accessing or using our Service, you agree to be bound by these Terms. If you do not
          agree to these Terms, please do not use our Service. These Terms constitute a
          legally binding agreement between you and Protego AI Inc.
        </p>
      </section>

      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-10 mb-4">
          2. Description of Service
        </h2>
        <p>
          Protego AI is an account health monitoring and risk intelligence platform designed
          to help Amazon sellers understand and proactively manage enforcement risks
          associated with their Amazon seller accounts. Our Service provides:
        </p>
        <ul className="list-disc pl-6 space-y-2 mt-3 text-white/75">
          <li>
            Risk scoring based on analysis of account health metrics obtained through the
            Amazon Selling Partner API
          </li>
          <li>Early warning signals and alerts for potential policy violations</li>
          <li>Educational content and insights regarding Amazon&apos;s enforcement patterns</li>
          <li>Account health audits and advisory reports</li>
        </ul>
        <p className="mt-4">
          The Service is intended for informational and educational purposes only. We do not
          provide legal advice, and nothing in our Service constitutes a guarantee of any
          specific outcome with respect to your Amazon seller account.
        </p>
      </section>

      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-10 mb-4">
          3. Eligibility
        </h2>
        <p>
          You must be at least 18 years of age to use our Service. By using our Service, you
          represent and warrant that you are at least 18 years old and have the legal capacity
          to enter into these Terms. If you are using the Service on behalf of a business
          entity, you represent and warrant that you have the authority to bind that entity to
          these Terms.
        </p>
      </section>

      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-10 mb-4">
          4. Account Registration
        </h2>
        <p>
          To access certain features of the Service, you may be required to register for an
          account. You agree to:
        </p>
        <ul className="list-disc pl-6 space-y-2 mt-3 text-white/75">
          <li>Provide accurate, current, and complete information during registration</li>
          <li>Maintain and promptly update your account information</li>
          <li>Keep your password and account credentials confidential</li>
          <li>Notify us immediately of any unauthorized use of your account</li>
          <li>Accept responsibility for all activities that occur under your account</li>
        </ul>
        <p className="mt-4">
          We reserve the right to suspend or terminate your account if we suspect any
          unauthorized use or violation of these Terms.
        </p>
      </section>

      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-10 mb-4">
          5. Amazon SP-API Authorization
        </h2>
        <p>
          To use the core monitoring features of our Service, you must authorize us to access
          your Amazon seller data through the Amazon Selling Partner API. By granting this
          authorization, you:
        </p>
        <ul className="list-disc pl-6 space-y-2 mt-3 text-white/75">
          <li>
            Confirm that you are the authorized Amazon seller account holder or have
            permission to authorize SP-API access
          </li>
          <li>
            Agree that we may access the data categories specified during the authorization
            process
          </li>
          <li>
            Acknowledge that you may revoke SP-API access at any time through your Amazon
            Seller Central settings
          </li>
        </ul>
        <p className="mt-4">
          You are solely responsible for ensuring that your use of our Service complies with
          Amazon&apos;s Terms of Service, Selling Policies, and any applicable agreements with
          Amazon.
        </p>
      </section>

      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-10 mb-4">
          6. Acceptable Use
        </h2>
        <p>
          You agree to use the Service only for lawful purposes and in accordance with these
          Terms. You agree not to:
        </p>
        <ul className="list-disc pl-6 space-y-2 mt-3 text-white/75">
          <li>Use the Service for any unlawful purpose or in violation of any applicable regulations</li>
          <li>Attempt to circumvent, hack, or interfere with any security features of the Service</li>
          <li>Scrape, copy, or redistribute any content from the Service without our express written consent</li>
          <li>Use the Service to engage in any conduct that could harm, disrupt, or overburden our infrastructure</li>
          <li>Misrepresent your identity or affiliation with any person or organization</li>
          <li>Use the Service to develop a competing product or service</li>
          <li>Share your account credentials with any third party</li>
        </ul>
      </section>

      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-10 mb-4">
          7. Intellectual Property
        </h2>
        <p>
          All content, features, and functionality of the Service — including but not limited
          to the Protego AI platform, risk scoring methodology, software, text, graphics,
          logos, and data compilations — are owned by Protego AI Inc. or its licensors and are
          protected by copyright, trademark, and other intellectual property laws. Nothing in
          these Terms grants you any right to use our intellectual property other than as
          expressly authorized herein.
        </p>
      </section>

      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-10 mb-4">
          8. Disclaimer of Warranties
        </h2>
        <p className="uppercase text-sm tracking-wider text-white/80 font-semibold">
          The Service is provided &quot;as is&quot; and &quot;as available&quot; without
          warranties of any kind, either express or implied. To the fullest extent permitted
          by applicable law, we disclaim all warranties, including but not limited to implied
          warranties of merchantability, fitness for a particular purpose, and non-infringement.
        </p>
        <p className="mt-4">
          We do not warrant that: (a) the Service will be uninterrupted or error-free; (b)
          defects will be corrected; (c) the Service is free of viruses or other harmful
          components; (d) the results or information obtained through the Service will be
          accurate, reliable, or complete; or (e) the Service will prevent adverse actions by
          Amazon against your seller account.
        </p>
        <p className="mt-4">
          Risk scores, alerts, and analysis provided by our Service are based on available
          data and our proprietary methodologies. They are educational indicators only and
          should not be relied upon as guarantees of any specific outcome.
        </p>
      </section>

      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-10 mb-4">
          9. Limitation of Liability
        </h2>
        <p className="uppercase text-sm tracking-wider text-white/80 font-semibold">
          To the maximum extent permitted by applicable law, Protego AI Inc. and its officers,
          directors, employees, and agents shall not be liable for any indirect, incidental,
          special, consequential, or punitive damages, including but not limited to loss of
          profits, revenue, data, business, or goodwill, arising out of or related to your use
          of the Service, even if we have been advised of the possibility of such damages.
        </p>
        <p className="mt-4 uppercase text-sm tracking-wider text-white/80 font-semibold">
          In no event shall our total liability to you for all claims related to the Service
          exceed the greater of (a) the amount you paid to us in the twelve (12) months
          preceding the event giving rise to liability, or (b) one hundred dollars ($100).
        </p>
      </section>

      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-10 mb-4">
          10. Indemnification
        </h2>
        <p>
          You agree to indemnify, defend, and hold harmless Protego AI Inc. and its officers,
          directors, employees, and agents from and against any claims, liabilities, damages,
          losses, and expenses (including reasonable legal fees) arising out of or related to:
          (a) your use of the Service; (b) your violation of these Terms; (c) your violation
          of any third-party rights, including Amazon&apos;s Selling Policies; or (d) your
          violation of any applicable law or regulation.
        </p>
      </section>

      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-10 mb-4">
          11. Termination
        </h2>
        <p>
          We may suspend or terminate your access to the Service at any time, with or without
          cause, and with or without notice. Upon termination, your right to use the Service
          will immediately cease. Provisions of these Terms that by their nature should
          survive termination shall survive, including but not limited to intellectual
          property rights, disclaimers, indemnification, and limitations of liability.
        </p>
      </section>

      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-10 mb-4">
          12. Modifications to the Service and Terms
        </h2>
        <p>
          We reserve the right to modify or discontinue the Service, temporarily or
          permanently, at any time with or without notice. We also reserve the right to update
          these Terms at any time. We will notify you of material changes by posting updated
          Terms on our website or via email. Your continued use of the Service following any
          such changes constitutes your acceptance of the new Terms.
        </p>
      </section>

      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-10 mb-4">
          13. Governing Law and Dispute Resolution
        </h2>
        <p>
          These Terms shall be governed by and construed in accordance with the laws of the
          Province of Ontario and the federal laws of Canada applicable therein, without
          regard to conflict of law provisions. Any dispute arising from these Terms or your
          use of the Service shall be submitted to binding arbitration or resolved in the
          courts of competent jurisdiction in Ontario, Canada. You and we agree to waive any
          right to a class action lawsuit or class-wide arbitration.
        </p>
      </section>

      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-10 mb-4">
          14. Relationship of the Parties
        </h2>
        <p>
          Nothing in these Terms creates a partnership, joint venture, agency, or employment
          relationship between you and Protego AI Inc. We are an independent contractor
          providing a commercial software-as-a-service platform.
        </p>
      </section>

      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-10 mb-4">
          15. Entire Agreement
        </h2>
        <p>
          These Terms, together with our Privacy Policy, constitute the entire agreement
          between you and us regarding the Service and supersede all prior agreements,
          understandings, and representations, whether written or oral, relating to the
          subject matter hereof.
        </p>
      </section>

      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-10 mb-4">
          16. Contact Us
        </h2>
        <p>If you have questions about these Terms, please contact us:</p>
        <div className="mt-4 bg-[#121316] border border-white/10 rounded-xl p-5">
          <p className="font-bold text-white mb-1">Protego AI Inc.</p>
          <p>
            Email:{" "}
            <a
              href="mailto:hello@protegoos.com"
              className="text-[#F04632] font-semibold hover:underline"
            >
              hello@protegoos.com
            </a>
          </p>
          <p>
            Website:{" "}
            <a
              href="https://section3.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#F04632] font-semibold hover:underline"
            >
              https://section3.ai
            </a>
          </p>
        </div>
      </section>
    </LegalLayout>
  );
}
